import React from "react";
import { Heart, RefreshCw, Briefcase } from "lucide-react";
import { useNavigate } from "react-router-dom";

const DonationSection = () => {
  const navigate = useNavigate();

  const navigateToDonate = () => {
    navigate("/donate");
  };

  return (
    <div className="donation-section">
      <style jsx>{`
        .donation-section {
          background: linear-gradient(
            135deg,
            #2e313c 0%,
            #2e313c 50%,
            #2e313c 100%
          );
          min-height: 100vh;
          padding: 4rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }

        .donation-section::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: radial-gradient(
              circle at 20% 20%,
              rgba(255, 255, 255, 0.1) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 80% 80%,
              rgba(255, 255, 255, 0.05) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 40% 60%,
              rgba(255, 255, 255, 0.03) 0%,
              transparent 50%
            );
          pointer-events: none;
        }

        .container {
          max-width: 1200px;
          width: 100%;
          position: relative;
          z-index: 1;
        }

        .header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .title {
          font-size: 3rem;
          font-weight: 700;
          color: white;
          margin-bottom: 1rem;
          letter-spacing: -0.025em;
          line-height: 1.2;
        }

        .title-underline {
          width: 80px;
          height: 4px;
          background: linear-gradient(90deg, var(--accent), var(--yellow));
          margin: 0 auto 2rem;
          border-radius: 2px;
        }

        .subtitle {
          font-size: 1.25rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
          font-weight: 300;
        }

        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
          margin-bottom: 4rem;
        }

        .donation-card {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 16px;
          padding: 2.5rem;
          text-align: center;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .donation-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.1),
            transparent
          );
          transition: left 0.5s ease;
        }

        .donation-card:hover {
          transform: translateY(-5px);
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
        }

        .donation-card:hover::before {
          left: 100%;
        }

        .card-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(90deg, var(--accent), var(--yellow));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }

        .card-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: white;
          margin-bottom: 1rem;
          letter-spacing: -0.025em;
        }

        .card-description {
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.6;
          font-weight: 300;
        }

        .cta-button {
          background: linear-gradient(90deg, var(--accent), var(--yellow));
          color: #ffff;
          border: none;
          padding: 1rem 2.5rem;
          border-radius: 50px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05);
          position: relative;
          overflow: hidden;
        }

        .cta-button::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.3),
            transparent
          );
          transition: left 0.5s ease;
        }

        .cta-button:active {
          transform: translateY(0);
        }

        @media (max-width: 768px) {
          .donation-section {
            padding: 2rem 1rem;
          }

          .title {
            font-size: 2rem;
          }

          .subtitle {
            font-size: 1.1rem;
          }

          .cards-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }

          .donation-card {
            padding: 2rem;
          }

          .card-icon {
            width: 56px;
            height: 56px;
          }
        }
      `}</style>

      <div className="container">
        <div className="header">
          <h2 className="title">Support Our Mission</h2>
          <div className="title-underline"></div>
          <p className="subtitle">
            Your contribution helps us empower communities through education and
            collaborative initiatives, creating lasting positive impact for
            generations to come.
          </p>
        </div>

        <div className="cards-grid">
          <div className="donation-card">
            <div className="card-icon">
              <Heart size={28} color="#2e313c" />
            </div>
            <h3 className="card-title">One-Time Donation</h3>
            <p className="card-description">
              Make a direct impact with a one-time contribution of any amount to
              support our programs and help us reach more communities in need.
            </p>
          </div>

          <div className="donation-card">
            <div className="card-icon">
              <RefreshCw size={28} color="#2e313c" />
            </div>
            <h3 className="card-title">Monthly Giving</h3>
            <p className="card-description">
              Become a sustaining supporter with a recurring monthly donation to
              help us plan for the future and ensure consistent program
              delivery.
            </p>
          </div>

          <div className="donation-card">
            <div className="card-icon">
              <Briefcase size={28} color="#2e313c" />
            </div>
            <h3 className="card-title">Corporate Sponsorship</h3>
            <p className="card-description">
              Partner with us through tiered sponsorship packages that align
              with your company's values and corporate social responsibility
              goals.
            </p>
          </div>
        </div>

        <button className="cta-button" onClick={() => navigateToDonate()}>
          Donate Now
        </button>
      </div>
    </div>
  );
};

export default DonationSection;
